import { GenerateABDPoleData } from "@/api/generate-abd-pole";
import { FormContainer } from "@/components/form/form-container";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ImageList, ImageListItem, ImageListItemBar } from "@mui/material";
import { useForm } from "react-hook-form";
import { ImageWithPreview } from "./ImageWithPreview";
import { useState } from "react";
import { MediaData } from "@/api/media";
import { CheckCircle2Icon, LucideUpload, XCircleIcon } from "lucide-react";
import { FieldFile } from "@/components/form/field-file";
import { useImageCompressor } from "@/utils/hook/useImageCompressor";
import { DefaultButton } from "@/components/ui/button";
import {
  OPMPoleValidationData,
  useOPMPoleValidate,
  useRFS,
  useRFSStatusUpdate,
} from "@/api/rfs";
import { ROLE, STATUS } from "@/utils/constant";
import { swalErrorDefault, swalLoading, swalSuccess } from "@/utils/lib/swal";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { OPMPoleData } from "@/api/opm-pole";
import { useCurrentUser } from "../auth/useCurrentUser";
import { hasCommonElement } from "@/utils/helper/hasCommonElement";
import { createRFSStatusUpdater } from "./utils/rfsStatusUpdater";
import { useValidation } from "./contexts/validationContext";
import { overridePoleInvalid, overridePoleValid } from "./utils/dataMappers";
import { OverallStatus } from "./providers/ValidationProvider";

interface PoleAccordionItemsProps {
  abdPoles?: GenerateABDPoleData[];
  opmPoles?: OPMPoleData[];
  id: string;
}

export default function PoleAccordionItems(props: PoleAccordionItemsProps) {
  const { opmPoles, abdPoles, id } = props;

  if (!abdPoles || abdPoles.length === 0) {
    return null;
  }

  return (
    <>
      {abdPoles?.map((abdPole) => {
        const opmPole = opmPoles?.find(
          (opmFat) => opmFat.poleId === abdPole.poleId,
        );
        return (
          <AccordionItem value={abdPole.id} key={abdPole.id}>
            <AccordionTrigger>
              <span className="flex items-center gap-2 dark:text-neutral-300">
                Pole ID: {abdPole.poleId}
                {opmPole && opmPole.isValid && (
                  <ImageStatusIcon valid={opmPole.isValid} />
                )}
              </span>
            </AccordionTrigger>
            <AccordionContent className="flex gap-x-2">
              <PoleForm
                rfsId={id}
                opmPole={opmPole}
                poleId={abdPole.poleId}
                opmPoles={opmPoles}
              />
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </>
  );
}

function PoleForm({
  rfsId,
  poleId,
  opmPole,
  opmPoles,
}: {
  rfsId: string;
  poleId: string;
  opmPole?: OPMPoleData;
  opmPoles?: OPMPoleData[];
}) {
  const [polePhotoMedia, setPolePhotoMedia] = useState<MediaData | undefined>(
    opmPole?.imagePoleIdMedia,
  );

  const { mutateAsync: compress } = useImageCompressor();
  const { data: rfs } = useRFS({ id: rfsId });
  const { mutate: submit, isPending } = useOPMPoleValidate();
  const queryClient = useQueryClient();
  const { roles } = useCurrentUser();
  const { mutate: updateStatus } = useRFSStatusUpdate();

  const { overallStatus: currentOverallStatus, getStatusForSubmit } =
    useValidation();

  const form = useForm({ mode: "onChange" });

  const currentPolePhoto = form.watch("pole_photo");

  const polePhotoPath =
    currentPolePhoto?.stream || opmPole?.imagePoleIdMedia?.stream;

  const isViewOnly = hasCommonElement(roles, [ROLE.VENDOR_VIEW]);

  function handleStatus(nextStatus: OverallStatus) {
    const statusUpdater = createRFSStatusUpdater({
      rfsId,
      overallStatus: nextStatus,
      rfsData: rfs,
      updateStatusMutation: updateStatus,
      prefix: "opm",
      options: {
        onError: () => {
          swalErrorDefault.fire({
            title: "Status Update Failed",
            text: "You failed to update the RFS status. Please try again.",
          });
        },
      },
    });
    statusUpdater();
  }

  function handleFileDragOver(e: React.DragEvent<HTMLLabelElement>) {
    e.preventDefault();
  }

  function handleFileDrop(e: React.DragEvent<HTMLLabelElement>) {
    e.preventDefault();

    const files = e.dataTransfer.files;
    if (!files.length) {
      return;
    }
    if (files.length > 1) {
      throw new Error("Only one file is allowed");
    }

    const changeEvent = new Event("change", { bubbles: true });
    const input = e.currentTarget.querySelector("input");
    if (!input) {
      return;
    }
    input.files = files;
    input.dispatchEvent(changeEvent);

    e.dataTransfer.clearData();
  }

  function handleSubmit() {
    if (!currentPolePhoto) {
      console.error("No pole photo to submit");
      return;
    }

    swalLoading.fire();
    const data: OPMPoleValidationData = {
      opmPole: {
        poleId: poleId,
        imagePoleIdMedia: {
          id: polePhotoMedia?.id,
          originalFileName: polePhotoMedia?.originalFileName,
          path: polePhotoMedia?.path,
        },
      },
    };
    if (opmPole?.id) {
      data.opmPole.id = opmPole.id;
    }

    submit(
      {
        id: rfsId,
        data: data,
      },
      {
        onSuccess: () => {
          form.clearErrors();
          swalSuccess.fire({
            title: "Validation Successful",
            text: "You have successfully submitted Pole.",
          });

          // Cache opmPoles baru di-invalidate di onSettled, jadi hitung status
          // dari data yang sudah di-override supaya pole terakhir ikut terhitung.
          const nextStatus =
            getStatusForSubmit?.({
              opmPoles: overridePoleValid(opmPoles, poleId),
            })?.overallStatus ?? currentOverallStatus;
          handleStatus(nextStatus);
        },
        onError: (error) => {
          if (axios.isAxiosError(error)) {
            const messages: { [key: string]: string }[] =
              error.response?.data?.data ?? [];

            for (const message of messages) {
              const imagePoleIdMediaMessage = message.imagePoleIdMedia;

              if (imagePoleIdMediaMessage) {
                form.setError("pole_photo", {
                  message: imagePoleIdMediaMessage,
                });
              }
            }

            // Submit ditolak: cermin dari onSuccess — tambal data secara lokal lalu
            // hitung ulang, tanpa menunggu refetch. onSettled yang menyegarkan UI.
            if (error.response?.status === 400) {
              const nextStatus =
                getStatusForSubmit?.({
                  opmPoles: overridePoleInvalid(opmPoles, poleId),
                })?.overallStatus ?? currentOverallStatus;
              handleStatus(nextStatus);
            }
          }

          swalErrorDefault.fire({
            title: "Validation Failed",
            text: "You failed to perform data validation. Please check the data then try again.",
          });
        },
        onSettled: () => {
          queryClient.invalidateQueries({
            queryKey: ["opmPoles", { rfsId }],
          });
          queryClient.invalidateQueries({
            queryKey: ["getGenerateABDPoles", { rfsId }],
          });
        },
      },
    );
  }

  const acceptedExtensions = ".jpg,.jpeg";
  const isReadonly =
    rfs?.statusRfsSubmission === STATUS.RFS.GENERATING_HPDB || isViewOnly;

  return (
    <>
      <FormContainer form={form} className="w-full">
        <ImageList cols={5} rowHeight={240} className="flex-grow">
          <ImageListItem>
            <ImageListItemBar
              title={
                <span className="flex items-center gap-1">
                  Pole Photo{" "}
                  <ImageStatusIcon valid={opmPole?.poleIdStatus ?? null} />
                </span>
              }
              className="!bg-slate-900/30"
              position="top"
            />
            {(currentPolePhoto?.path || opmPole?.imagePoleId) && (
              <ImageWithPreview src={polePhotoPath} alt="Pole Photo" />
            )}
            <div className="mt-auto">
              {form.formState.errors.pole_photo && (
                <span className="text-sm font-medium text-red-500 dark:text-red-900">
                  {form.formState.errors.pole_photo?.message?.toString()}
                </span>
              )}

              <label
                className={`mt-auto flex cursor-pointer flex-col border-2 border-dashed py-2 text-center dark:text-neutral-300 ${
                  isReadonly && "sr-only"
                }`}
                onDragOver={handleFileDragOver}
                onDrop={handleFileDrop}
              >
                <LucideUpload className="mx-auto" />
                click or drag here
                <FieldFile
                  isVisuallyHidden
                  name="pole_photo"
                  label="Pole Photo"
                  media={{
                    target: "/opm/pole",
                    after: (result) => setPolePhotoMedia(result),
                    before: (files) => {
                      const compressedFile = compress(files[0]);
                      return compressedFile;
                    },
                  }}
                  accept={acceptedExtensions}
                />
              </label>
            </div>
          </ImageListItem>
        </ImageList>
      </FormContainer>
      <div className="flex w-24 flex-col justify-center gap-y-2">
        <DefaultButton
          type="button"
          mood="axiata-blue"
          className="w-full"
          disabled={!polePhotoPath || isViewOnly}
          loading={isPending}
          onClick={handleSubmit}
        >
          Submit
        </DefaultButton>
      </div>
    </>
  );
}

function ImageStatusIcon({ valid }: { valid: boolean | null }) {
  if (valid === null || valid === undefined) {
    return null;
  }
  if (valid === true) {
    return <CheckCircle2Icon size={16} className="text-green-500" />;
  }
  return <XCircleIcon size={16} className="text-red-500" />;
}
